<template>
    <div class="page-wrapper">
        <div class="page-title">
            <CIcon name="cil-paw" /> Species Wise Report
            <span class="page-title-badge" v-if="speciesCount > 0">{{ speciesCount }} species</span>
        </div>

        <CRow>
            <CCol md="12" class="pad-r-0 pad-l-0">
                <data-filter
                    :tableLength="totalRecords"
                    v-model="filter"
                ></data-filter>
            </CCol>
        </CRow>
        <div v-if="loading" class="loader-area">
            <pulse-loader :loading="loading" color="#2E7D32"></pulse-loader>
        </div>
        <CRow v-if="!loading">
            <CCol md="12" class="pad-r-0 pad-l-0">
                <CCard>
                    <CCardHeader class="no-backdrop">
                        <div class="d-flex justify-content-between align-items-center w-100">
                            <div>
                                <h4 class="card-title mb-0">Species Breakdown</h4>
                                <div class="small text-danger mt-1" v-if="speciesCount === 0">No records found for applied filters!</div>
                            </div>
                            <json-excel
                                :data="download"
                                :name="filename"
                            >
                                <CButton color="success" size="sm">
                                    <CIcon name="cil-cloud-download" /> Export Excel
                                </CButton>
                            </json-excel>
                        </div>
                    </CCardHeader>
                    <CCardBody v-if="speciesCount > 0">
                        <b-table
                            hover
                            responsive
                            select-mode="single"
                            :items="displacementData"
                            :fields="fields"
                            :sort-by.sync="sortBy"
                            :sort-desc.sync="sortDesc"
                            class="clickable-table"
                            id="displacementTable"
                        >
                            <template v-slot:cell(_id)="data">
                                <b>{{ data.item._id }}</b>
                            </template>
                            <template #thead-top>
                                <b-tr>
                                    <b-th><span class="sr-only">Name</span></b-th>
                                    <b-th><span class="sr-only">Total</span></b-th>
                                    <b-th variant="secondary" colspan="3" class="text-uppercase small">SEX</b-th>
                                    <b-th variant="primary" colspan="2" class="text-uppercase small">STAGE</b-th>
                                    <b-th variant="danger" colspan="7" class="text-uppercase small">OUTCOME</b-th>
                                </b-tr>
                            </template>
                        </b-table>
                    </CCardBody>
                </CCard>
            </CCol>
        </CRow>
    </div>
</template>

<script>
import DataFilter from './components/dataFilter.vue';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import axios from 'axios';
import store from '../store';
import JsonExcel from 'vue-json-excel';

export default {
    name: 'SpeciesWise',
    components: {
        DataFilter,
        PulseLoader,
        JsonExcel,
    },
    props: {
        small: true,
    },
    data() {
        return {
            baseURL: store.state.baseURL,
            filename: 'SaveAnimals_DB_SexWise_Data.xls',
            showFilter: true,

            sortBy: 'total',
            sortDesc: true,

            histogram: {
                percentage: [],
                values: [],
                chartOptions: {
                    chart: {
                        type: 'bar',
                        height: 600,
                    },
                    plotOptions: {
                        bar: {
                            borderRadius: 4,
                            horizontal: false,
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: this.graphLabel,
                    },
                    xaxis: {
                        categories: [],
                    },
                },
            },

            selected: {},
            filter: {
                fromDate: null,
                toDate: null,
                class: null,
                family: null,
                group: null,
                identity: null,
                scientific: null,
                zone: null,
                outcome: null,
                timeType: null,
                date: null,
                timeIntervalType: null,
                caseno: null,
                stage: null,
                facility: null,
                placeOfRescue: null,
                causeOfDisp: null,
            },
            graphColours: [
                '#e6194B',
                '#3cb44b',
                '#ffe119',
                '#4363d8',
                '#f58231',
                '#911eb4',
                '#42d4f4',
                '#f032e6',
                '#bfef45',
                '#808000',
            ],
            perPage: 12,
            currentPage: 1,
            loading: true,
            filterLoading: true,
            fields: [],
            download: [],
            totalRecords: 0,
            loadPage: 1,
            pagesAvailable: 1,
            speciesCount: 0,
            displacementData: [],
        };
    },
    beforeMount() {
        var d = new Date();
        var str = d.toISOString();
        str = str.substr(0, 10);
        str = str.replace(/-/g, '');
        this.filename = 'SexWiseData_SaveAnimals_DB_' + str + '.xls';
    },
    created() {
        this.selected = {
            sourceIntervalType: store.state.filters.sourceIntervalType,
            sourceType: store.state.filters.sourceType,
            date: store.state.filters.date,
            startDate: store.state.filters.startDate,
            endDate: store.state.filters.endDate,
            outcome: store.state.filters.outcome,
            zone: store.state.filters.zone,
            class: store.state.filters.class,
            family: store.state.filters.family,
            group: store.state.filters.group,
            identity: store.state.filters.identity,
            caseno: store.state.filters.caseno,
            scientific: store.state.filters.scientific,
            stage: store.state.filters.stage,
            facility: store.state.filters.facility,
            placeOfRescue: store.state.filters.placeOfRescue,
            causeOfDisp: store.state.filters.causeOfDisp,
            month: store.state.filters.month,
            year: store.state.filters.year,
        };

        this.filter = {
            fromDate: this.selected.startDate,
            toDate: this.selected.endDate,
            class: this.selected.class,
            family: this.selected.family,
            group: this.selected.group,
            identity: this.selected.identity,
            scientific: this.selected.scientific,
            zone: this.selected.zone,
            outcome: this.selected.outcome,
            timeType: this.selected.sourceType,
            date: this.selected.date,
            timeIntervalType: this.selected.sourceIntervalType,
            caseno: this.selected.caseno,
            stage: this.selected.stage,
            facility: this.selected.facility,
            placeOfRescue: this.selected.placeOfRescue,
            causeOfDisp: this.selected.causeOfDisp,
        };

        this.getRecords(true);
    },
    watch: {
        filter: {
            handler(newVal, oldVal) {
                this.getRecords(true);
            },
            deep: true,
        },
    },
    methods: {
        makeDownloadable(records, fields) {
            this.download = [];
            for (var x in records) {
                var tempItem = {};
                for (var i in fields) {
                    tempItem[fields[i].label] = records[x][fields[i].key];
                }
                this.download.push(tempItem);
            }
        },
        graphLabel(val, opts) {
            return val + "%(" + this.histogram.values[opts.dataPointIndex] + ")";
        },
        getRecords(reset) {
            this.loading = true;
            axios({
                url: this.baseURL + "analytics/species-wise",
                headers: {
                    authorization: store.state.token,
                },
                data: this.filter,
                method: "POST",
            })
                .then((res) => {
                    if (res.data.status) {
                        this.histogram.series = [];
                        this.histogram.chartOptions.xaxis.categories = [];
                        this.histogram.values = [];

                        this.fields = res.data.fields;
                        this.displacementData = res.data.data;
                        this.speciesCount = res.data.data.length;

                        this.makeDownloadable(res.data.data, res.data.fields);

                        this.totalRecords = res.data.totalRecords;

                        this.loading = false;
                    } else {
                        this.fields = [];
                        this.displacementData = [];
                        this.speciesCount = 0;
                        this.totalRecords = 0;
                        this.$notify({
                            group: "notification",
                            type: "error",
                            title: "Important message",
                            text: res.data.message,
                        });
                        this.loading = false;
                    }
                })
                .catch((err) => {
                    console.log(err);
                    this.fields = [];
                    this.displacementData = [];
                    this.speciesCount = 0;
                    this.totalRecords = 0;
                    this.$notify({
                        group: "notification",
                        type: "error",
                        title: "Important message",
                        text: err.status,
                    });
                    this.loading = false;
                });
        },
        onSelectRow(row) {
            this.openReport(row[0]._id);
        },
        selectAnimal(item) {},
        openReport(id) {
            window.open(`/existing-case/${id}`, "_blank");
        },
    },
};
</script>

<style scoped>
.pad-r-0 {
    padding-right: 0px !important;
}
.pad-l-0 {
    padding-left: 0px !important;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
